import React, { useState, useContext, useCallback, useRef } from 'react';
import { AuthContext } from 'utils/AuthContext';
import { FlexLeftCol } from 'styles/global-style';
import Button from 'components/Button';
import Modal from 'components/Modal';
import { parseJiraCSV } from 'utils/jiraParser';
import { mapJiraEpics, getAvailableEpics } from 'utils/jiraEpicMapper';
import { executeImport } from 'utils/jiraImporter';
import JiraMappingModal from 'components/JiraMappingModal';
import { useNavigate } from 'react-router-dom';

export default function Import() {
  const { supabase, user, refresh } = useContext(AuthContext);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [mapping, setMapping] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const [epicMappings, setEpicMappings] = useState(null);
  const [availableEpics, setAvailableEpics] = useState([]);
  const [strategy, setStrategy] = useState(null);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [projectType, setProjectType] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState('');
  
  // Get project type from user.proj.type
  const getProjectType = useCallback(() => {
    return user?.proj?.type || null;
  }, [user]);
  
  const handleFileSelect = useCallback(async (event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;
    
    if (!selectedFile.name.endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }
    
    setFile(selectedFile);
    setError(null);
    setParsedData(null);
    setEpicMappings(null);
    
    // Show modal immediately
    setShowMappingModal(true);
    setLoadingStatus('Reading file...');
    
    // Read and parse file
    setParsing(true);
    try {
      const text = await selectedFile.text();
      setLoadingStatus('Parsing CSV file...');
      const parsed = parseJiraCSV(text);
      setParsedData(parsed);
      
      // Get project type
      setLoadingStatus('Getting project information...');
      const type = getProjectType();
      setProjectType(type);
      
      // Get available epics
      setLoadingStatus('Loading available epics...');
      const epics = await getAvailableEpics(supabase, user, type);
      setAvailableEpics(epics);
      
      // Map JIRA epics
      setLoadingStatus('Mapping epics...');
      setMapping(true);
      const mappingResult = await mapJiraEpics(parsed.epics, supabase, user, type);
      setStrategy(mappingResult.strategy);
      
      // Prepare epic mappings - they already have peepzSubepic: null from mapper
      setEpicMappings(mappingResult.mappings);
      setLoadingStatus('');
    } catch (err) {
      console.error('Error parsing CSV:', err);
      setError(`Error parsing CSV: ${err.message}`);
      setLoadingStatus('');
    } finally {
      setParsing(false);
      setMapping(false);
    }
  }, [supabase, user, getProjectType]);
  
  const handleMappingChange = useCallback((updatedMappings) => {
    setEpicMappings(updatedMappings);
  }, []);
  
  const handleImport = useCallback(async (mappingData) => {
    if (!parsedData || !mappingData) return;
    
    setImporting(true);
    setError(null);
    
    try {
      // Execute import - task mappings will be converted from epic indices to subepic IDs during import
      const result = await executeImport(
        supabase,
        user,
        parsedData,
        mappingData.epicMappings,
        mappingData.taskMappings, // Map with epic indices as values
        strategy,
        mappingData.replaceAllWork || false // Option to replace all current work
      );
      
      if (result.success) {
        // Refresh data
        await refresh();
        
        // Show success message
        alert(`Import successful!\n\nCreated:\n- ${result.summary.epics} Epics\n- ${result.summary.subepics} Subepics\n- ${result.summary.tasks} Tasks`);
        
        // Close modal and reset
        setShowMappingModal(false);
        setFile(null);
        setParsedData(null);
        setEpicMappings(null);
        setLoadingStatus('');
        // Reset file input so the same file can be selected again
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        
        // Navigate to project page to see imported items
        navigate('/auth/project');
      } else {
        setError(`Import failed: ${result.error}`);
      }
    } catch (err) {
      console.error('Error during import:', err);
      setError(`Import error: ${err.message}`);
    } finally {
      setImporting(false);
    }
  }, [parsedData, strategy, supabase, user, refresh, navigate]);
  
  const handleCancel = useCallback(() => {
    setShowMappingModal(false);
    setFile(null);
    setParsedData(null);
    setEpicMappings(null);
    setError(null);
    setLoadingStatus('');
    // Reset file input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);
  
  return (
    <FlexLeftCol>
      <h2>Import from JIRA</h2>
      
      <div style={{ marginBottom: 20 }}>
        <p style={{ color: '#666', marginBottom: 16 }}>
          Upload a CSV file exported from JIRA to import work items into Peepz.
        </p>
        
        <div style={{ marginBottom: 16 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            id="jira-csv-input"
          />
          <label htmlFor="jira-csv-input">
            <Button 
              text={file ? file.name : "Choose CSV File"} 
              callback={() => fileInputRef.current?.click()}
              color="purple"
            />
          </label>
        </div>
        
        {parsing && (
          <div style={{ color: '#666', fontStyle: 'italic' }}>
            Parsing CSV file...
          </div>
        )}
        
        {error && (
          <div style={{ 
            background: '#f8d7da', 
            padding: 12, 
            borderRadius: 8, 
            border: '1px solid #dc3545',
            color: '#721c24',
            marginTop: 12
          }}>
            {error}
          </div>
        )}
        
        {parsedData && !showMappingModal && (
          <div style={{ 
            background: '#d4edda', 
            padding: 12, 
            borderRadius: 8, 
            border: '1px solid #28a745',
            color: '#155724',
            marginTop: 12
          }}>
            <strong>CSV parsed successfully!</strong><br />
            Found: {parsedData.epics.length} Epics, {parsedData.stories.length} Stories, {parsedData.tasks.length} Tasks
          </div>
        )}
      </div>
      
      {showMappingModal && (
        <Modal closeCb={handleCancel}>
          {parsing || mapping || !parsedData || !epicMappings ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <div style={{ fontSize: 18, marginBottom: 12, color: '#000' }}>
                {loadingStatus || (parsing ? 'Parsing CSV file...' : mapping ? 'Mapping epics...' : 'Loading...')}
              </div>
              <div style={{ color: '#666', fontSize: 14 }}>
                Please wait while we process your JIRA export.
              </div>
            </div>
          ) : (
            <JiraMappingModal
              parsedData={parsedData}
              epicMappings={epicMappings}
              availableEpics={availableEpics}
              strategy={strategy}
              onMappingChange={handleMappingChange}
              onImport={handleImport}
              onCancel={handleCancel}
            />
          )}
        </Modal>
      )}
      
      {importing && (
        <div style={{ 
          position: 'fixed', 
          top: '50%', 
          left: '50%', 
          transform: 'translate(-50%, -50%)',
          background: '#fff',
          padding: 30,
          borderRadius: 14,
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          zIndex: 10000
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 18, marginBottom: 12 }}>Importing...</div>
            <div style={{ color: '#666' }}>Please wait while we create work items.</div>
          </div>
        </div>
      )}
    </FlexLeftCol>
  );
}

